<script setup lang="ts">
import type { ComputedRef, Ref } from 'vue'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import CEntitySelector from '@lib/components/CEntitySelector/CEntitySelector.vue'
import CUserAvatar from '@lib/components/CUserAvatar/CUserAvatar.vue'
import CCard from '@lib/components/CCard/CCard.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CSelect from '@lib/components/CSelect/CSelect.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import { RecycleScroller } from 'vue-virtual-scroller'
import type { VueVirtualScroller } from '@lib/types/vueVirtualScroller'
import type { InstanceMembershipRoleEnum } from '@openapi'

import type {
  CollaborationItem,
  CollaborationNormalized,
  GroupSelectModel,
  InstanceMembershipItem,
  InstanceMembershipNormalized,
  Item,
  ItemNormalized,
  Role,
  UserSelectModel,
} from './CUsersGroupsAccessManage.types'
import { splitUserName } from '@lib/util/user'

interface Props {
  selectQuery: (params?: any) => { data: Ref; isFetching: Ref<boolean> }
  itemType: 'user' | 'group'
  items: Item[]
  roleOptions?: { value: Role; label: string }[]
  isAddingItemsMap?: Record<string, boolean>
  maxItemsToRender?: number
  maxSelectItemsToRender?: number
  title?: string
}

const props = withDefaults(defineProps<Props>(), {
  items: Array,
  maxItemsToRender: 5,
  maxSelectItemsToRender: 5,
})

const emit = defineEmits<{
  (e: 'update', item: Item): void
  (e: 'delete', item: Item): void
  (e: 'select', scroller: VueVirtualScroller, selectItem: UserSelectModel | GroupSelectModel): Promise<void>
}>()

const { t } = useI18n()
const themeVars = useThemeVars()

const SCROLLER_ITEM_SIZE = 44

const scroller: Ref<VueVirtualScroller> = ref(null)

const itemsNormalized = computed(() => {
  return props.items.map((item) => {
    if ('content_distribution_group' in item) {
      const { content_distribution_group, ...rest } = item
      const type = 'file' in item || 'canvas' in item ? 'collaboration' : 'content_distribution_group_membership'
      return { ...rest, group: content_distribution_group, type }
    } else if ('user' in item && 'role' in item && ('file' in item || 'canvas' in item)) {
      const type = 'collaboration' // collaboration with user and no group
      return { ...item, type }
    } else if (
      'user' in item &&
      'role' in item &&
      (('instance_assignment_group' in item && !item.instance_assignment_group) ||
        !('instance_assignment_group' in item))
    ) {
      const type = 'instance_membership' // instance-membership with user and no group
      return { ...item, type }
    } else if ('instance_assignment_group' in item) {
      const type = 'instance_membership'
      const { instance_assignment_group, ...rest } = item
      return { ...rest, group: instance_assignment_group, type }
    }
  })
}) as ComputedRef<ItemNormalized[]>

//#region User

const renderUserSubtitle = (item: UserSelectModel) => {
  const isUser = 'email' in item

  if (isUser) {
    return item.email.trim()
  } else if ('user' in item && 'email' in item.user) {
    return item.user.email?.trim()
  } else {
    return undefined
  }
}

const renderUserTitle = (item: UserSelectModel) => {
  const isNotUser = 'user' in item

  return isNotUser ? item.user?.name.trim() : `${item.first_name ?? ''} ${item.last_name ?? ''}`.trim()
}

const userFilterFn = (item: UserSelectModel): boolean => {
  const isNotUser = 'user' in item

  return !props.items.some((ri) => {
    if (props.itemType === 'user' && 'user' in ri) {
      return isNotUser ? ri.user.id === item.user.id : ri.user.id === item.id
    }
    return false
  })
}

const userSearchFn = (search: string, item: UserSelectModel) => {
  const isNotUser = 'user' in item
  const isUser = 'email' in item

  const name = isNotUser
    ? item.user?.name.toLowerCase().trim() // Add parentheses to invoke the trim method
    : `${item.first_name?.toLowerCase() ?? ''} ${item.last_name?.toLowerCase() ?? ''}`.trim()

  let email = ''

  if (isUser) {
    email = item.email.trim().toLowerCase()
  } else if (isNotUser && 'email' in item.user) {
    email = item.user.email?.trim().toLowerCase() ?? ''
  }

  const searchLower = search?.toLowerCase()

  return name.includes(searchLower) || email.includes(searchLower)
}
//#endregion

//#region Group
const renderGroupTitle = (group: GroupSelectModel) => group.name

const groupFilterFn = (g: unknown): boolean => {
  const group = g as GroupSelectModel

  return !itemsNormalized.value.some((rin) => rin.group.id === group.id)
}
const groupSearchFn = (search: string, g: unknown) => {
  const group = g as GroupSelectModel
  const searchLower = search.toLowerCase()
  const name = group.name.toLowerCase()

  return name.includes(searchLower)
}
//#endregion

function updateItem(item: ItemNormalized, role: Role) {
  if (item.type === 'collaboration') {
    const { group, type, ...rest } = item
    emit('update', { ...rest, content_distribution_group: group, role } as CollaborationItem)
  } else if (item.type === 'instance_membership') {
    const { group, type, ...rest } = item
    emit('update', {
      ...rest,
      instance_assignment_group: group,
      role: role as InstanceMembershipRoleEnum,
    } as InstanceMembershipItem)
  }
}

function deleteItem(item: ItemNormalized) {
  if (item.type === 'content_distribution_group_membership' || item.type === 'collaboration') {
    const { group, type, ...rest } = item
    emit('delete', { ...rest, content_distribution_group: group } as Item)
  } else if (item.type === 'instance_assignment_group_membership' || item.type === 'instance_membership') {
    const { group, type, ...rest } = item
    emit('delete', { ...rest, instance_assignment_group: group } as Item)
  } else {
    emit('delete', item as Item)
  }
}

const displayTitle = computed(() => {
  if (props.title) return props.title

  return props.itemType === 'user'
    ? t('canvasUI.components.CUsersGroupsAccessManage.individualUsers', props.items.length)
    : t('canvasUI.components.CUsersGroupsAccessManage.groups', props.items.length)
})
</script>

<template>
  <CCard bordered size="medium">
    <div>
      <div class="flex items-center justify-between gap-1">
        <div class="flex gap-4 items-center">
          <CIcon :icon="itemType === 'user' ? 'user' : 'users'" size="14" />

          <div class="flex font-bold">
            {{ displayTitle }}
          </div>
        </div>

        <CEntitySelector
          v-if="itemType === 'group'"
          :disabledMap="isAddingItemsMap"
          :filterFn="groupFilterFn"
          :headerTitle="t('canvasUI.components.CUsersGroupsAccessManage.selectGroupFromList')"
          :maxItemsToRender="maxSelectItemsToRender"
          :query="selectQuery"
          :searchFn="groupSearchFn"
          :searchThreshold="maxSelectItemsToRender"
          :subtitle="(g: GroupSelectModel) => `${t('canvasUI.components.CUsersGroupsAccessManage.users', g.size)}`"
          :title="renderGroupTitle"
          @select="emit('select', scroller, $event as GroupSelectModel)"
        >
          <template #trigger>
            <CButton iconPlacement="left" secondary>
              {{ t('canvasUI.components.CUsersGroupsAccessManage.add') }}
              <template #icon>
                <CIcon icon="plus" size="16" />
              </template>
            </CButton>
          </template>
        </CEntitySelector>

        <CEntitySelector
          v-if="itemType === 'user'"
          :disabledMap="isAddingItemsMap"
          :filterFn="(item: unknown) => userFilterFn(item as UserSelectModel)"
          :headerTitle="t('canvasUI.components.CUsersGroupsAccessManage.selectUserFromList')"
          :maxItemsToRender="maxSelectItemsToRender"
          :query="selectQuery"
          :searchFn="(searchTerm, item: unknown) => userSearchFn(searchTerm, item as UserSelectModel)"
          :searchThreshold="maxSelectItemsToRender"
          :subtitle="renderUserSubtitle"
          :title="renderUserTitle"
          @select="emit('select', scroller, $event)"
        >
          <template #trigger>
            <CButton iconPlacement="left" secondary>
              {{ t('canvasUI.components.CUsersGroupsAccessManage.add') }}
              <template #icon>
                <CIcon icon="plus" size="small" />
              </template>
            </CButton>
          </template>
        </CEntitySelector>
      </div>
    </div>
    <RecycleScroller
      ref="scroller"
      v-slot="{ item: item }: { item: ItemNormalized }"
      :buffer="SCROLLER_ITEM_SIZE * maxItemsToRender * 2"
      class="mt-4 recycle-scroller"
      :itemSize="SCROLLER_ITEM_SIZE"
      :items="itemsNormalized"
      keyField="id"
      :style="
        itemsNormalized.length > maxItemsToRender ? { height: `${SCROLLER_ITEM_SIZE * maxItemsToRender}px` } : undefined
      "
    >
      <div class="flex items-center justify-between gap-1">
        <div class="flex items-center gap-1">
          <template v-if="itemType === 'user' && 'user' in item">
            <CUserAvatar
              :firstName="splitUserName(item.user?.name).first_name"
              :lastName="splitUserName(item.user?.name).last_name"
            />
            <div>
              <div v-if="item.user?.name.trim()" class="font-weight-bold">
                {{ splitUserName(item.user?.name)?.first_name }}
                {{ splitUserName(item.user?.name)?.last_name }}
              </div>
              <div v-else :style="{ color: themeVars.text2 }">{{ item?.user.email }}</div>
            </div>
          </template>
          <div v-if="itemType === 'group'" class="font-weight-bold">
            {{ item.group?.name }}
          </div>
        </div>

        <div class="flex items-center gap-4 mr-2">
          <div v-if="itemType === 'group'">
            {{ t('canvasUI.components.CUsersGroupsAccessManage.users', { n: item.group?.size || 0 }) }}
          </div>

          <CSelect
            v-if="roleOptions"
            :bordered="true"
            class="c-users-groups-access-manage__select"
            :disabled="(item as InstanceMembershipNormalized).role === 'org_group_member'"
            :options="roleOptions"
            :value="(item as CollaborationNormalized).role || (item as InstanceMembershipNormalized).role"
            @update:value="updateItem(item, $event)"
          />
          <CButton circle :color="themeVars.error2" @click="deleteItem(item)">
            <template #icon>
              <CIcon
                class="mr-[2px]"
                :color="themeVars.error"
                :icon="itemType === 'user' ? 'user-slash' : 'users-slash'"
                size="medium"
              />
            </template>
          </CButton>
        </div>
      </div>
    </RecycleScroller>
  </CCard>
</template>

<style lang="scss" scoped>
.c-users-groups-access-manage__select {
  width: 180px;
}
</style>
